import { User, Store, ExternalLink, Building2 } from 'lucide-react';
import { useDialogStore } from '@/store';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

type Props = {
  clientName: string;
  clientID: number;
  storeID: number;
};

function ClientColumn({ clientID, clientName, storeID }: Props) {
  const { openClientCardDialog } = useDialogStore();

  const handleClick = () => {
    openClientCardDialog(clientID);
  };
  
  const getStoreConfig = (storeID: number) => {
    switch(storeID) {
      case 1:
        return {
          name: 'مخزن القطاعي',
          color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
          borderColor: 'border-blue-300 dark:border-blue-800',
          icon: <Store className="h-3 w-3" />
        };
      case 2:
        return {
          name: 'مخزن الجملة',
          color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
          borderColor: 'border-emerald-300 dark:border-emerald-800',
          icon: <Building2 className="h-3 w-3" />
        };
      default:
        return {
          name: 'مخازن أخرى',
          color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400',
          borderColor: 'border-gray-300 dark:border-gray-700',
          icon: <Store className="h-3 w-3" />
        };
    }
  };

  const storeConfig = getStoreConfig(storeID);

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={`Open details for ${clientName}`}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleClick();
        }
      }}
      className={cn(
        "group relative flex w-full min-w-[220px] cursor-pointer items-start gap-3 rounded-lg",
        "bg-gradient-to-r from-background to-muted/10",
        "border border-border/50 px-3 py-2",
        "transition-all duration-200",
        "hover:border-primary/50 hover:bg-gradient-to-r hover:from-primary/5 hover:to-primary/10",
        "hover:shadow-md dark:hover:shadow-primary/10",
        "focus:outline-none focus:ring-2 focus:ring-primary/30"
      )}
    >
      <div className="mt-0.5 rounded-md bg-primary/10 p-1.5">
        <User className="h-3.5 w-3.5 text-primary" />
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <h3 className={cn(
            "line-clamp-1 text-sm font-medium",
            "text-foreground/90 group-hover:text-foreground",
            "transition-colors duration-200"
          )}>
            {clientName}
          </h3>
          <ExternalLink className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex-shrink-0" />
        </div>
        
        <div className="mt-1">
          <Badge 
            variant="outline"
            className={cn(
              "h-5 px-1.5 text-[10px] font-medium border",
              storeConfig.color,
              storeConfig.borderColor,
              "transition-all duration-200"
            )}
          >
            <span className="flex items-center gap-1">
              {storeConfig.icon}
              {storeConfig.name}
            </span>
          </Badge>
        </div>
      </div>
      
      <div className={cn(
        "absolute inset-0 rounded-lg bg-gradient-to-r from-primary/10 to-primary/5",
        "opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
      )} />
    </div>
  );
}

export default ClientColumn;